/**
 * `<RequireAuth>` — gate every non-auth surface behind a signed-in Clerk session.
 *
 * Before this existed, `<AppShell />` was mounted unconditionally at /* in
 * AppRoot, which meant /editor, /search, /chat, /settings rendered for any
 * visitor — the only thing distinguishing signed-in vs signed-out was a
 * cosmetic "Sign in" button in the top bar. The data path didn't notice
 * because the cloud SWA's Functions proxy injects a fixed loopback bearer
 * server-side, so every request authenticated regardless of who sent it.
 *
 * This component closes the visible hole:
 *   - signed in              → render children (the app shell)
 *   - signed out             → <Navigate> to /sign-in with redirect_url to
 *                              the page they were trying to reach
 *   - Clerk still loading    → render the boot-splash placeholder so the
 *                              shell doesn't flash unauthenticated content
 *                              while the session resolves
 *
 * Server-side Clerk JWT verification now exists (backend `aletheia.auth.clerk_jwt` +
 * `api/auth/loopback`): this component registers the session-token getter below so every
 * API request carries the user's Clerk JWT in `X-Aletheia-Session`, and the backend resolves
 * the caller to their own workspace. The loopback bearer remains the transport credential;
 * the session token is the *identity*. (Activation is gated on the backend's Clerk env and
 * the Clerk "customize session token" email/name claims — see the auth runbook.)
 */

import { useAuth, useUser } from '@clerk/clerk-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { configureDesktopDirectBackend } from '@/desktop/directBackend';
import { setSessionTokenGetter } from '@/api/client';

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const location = useLocation();

  // Multi-tenancy: register the getter the API client calls to attach the Clerk session JWT
  // (X-Aletheia-Session) on every request, so the backend resolves this user's own workspace.
  // Cleared when signed out / on unmount so a stale getter never leaks one user's token to
  // the next. `getToken()` returns the default session token — configure its email/name claims
  // in Clerk → Sessions → Customize session token for onboarding to succeed.
  useEffect(() => {
    if (!isLoaded) return undefined;
    setSessionTokenGetter(isSignedIn ? () => getToken() : null);
    return () => setSessionTokenGetter(null);
  }, [isLoaded, isSignedIn, getToken]);

  // Boot-time direct-backend wiring (#102 follow-up): the chat SSE stream needs to
  // bypass the buffering SWA proxy to actually stream. `configureDesktopDirectBackend`
  // sets ``window.__ALETHEIA_CHAT__`` so chatApi routes chat directly to Container
  // Apps. Previously this only fired when the user visited the MCP Integration
  // settings page — if they hadn't been there in the session, chat fell through to
  // the proxy and SSE arrived "all at once" instead of streaming.
  //
  // The function is idempotent + safe to call repeatedly + no-ops in browser mode
  // (isDesktopApp() check) and when no token is available (the next launch retries).
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const workspaceId = user?.id;
    if (workspaceId === undefined || workspaceId === '') return;
    void configureDesktopDirectBackend(workspaceId);
  }, [isLoaded, isSignedIn, user?.id]);

  // While Clerk is hydrating the session from the cookie / token store, hold
  // the render. A flash of the signed-out shell during this window would be
  // briefly indistinguishable from the bug this component fixes.
  if (!isLoaded) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100vh',
          background: 'var(--bg-0)',
          color: 'var(--fg-3)',
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: 13,
          letterSpacing: '0.04em',
        }}
      >
        <span>resolving session…</span>
      </div>
    );
  }

  if (!isSignedIn) {
    // Preserve the original path + query so Clerk's signInFallbackRedirectUrl
    // (set in AppRoot) takes the user back here after sign-in. Clerk reads
    // `redirect_url` from the query string by convention.
    const here = `${location.pathname}${location.search}`;
    const redirectUrl = here === '/' ? '/editor' : here;
    return (
      <Navigate
        to={`/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}`}
        replace
      />
    );
  }

  return <>{children}</>;
}
